import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from '../../../icons';
import { CommentThread, EmptyState, InlineNote, type CommentItem } from '../../../components/shared';
import { layerConfirm } from '../../../components/LayerPopup/LayerPopup';
import {
  createBoardPostComment, deleteBoardComment, listBoardPostComments, updateBoardComment,
  type BoardCommentDto,
} from '../../../api/board-comments';
import type { BoardPostDetail } from '../../../api/boards';
import { useAuthStore } from '../../../store/authStore';
import { tBoards } from '../../../lib/boards-i18n';
import { countComments } from './post-model';

/**
 * 댓글 탭 — 정본 E10 `CommentThread` 첫 사용처 (시안 v1 `post-tab-comments` · `post-comments-locked` · U3).
 *
 * ⚠⚠ **R12 선행 조건** — 기존 댓글 API 가 다중 게시판 읽기 권한을 확인하지 않는다. 서버 수정 전 배포 금지.
 *
 * ★ 권한 표시는 서버 규칙을 그대로 옮긴다 — 수정은 **댓글 작성자만**, 삭제는 **댓글 작성자 · 글 작성자 · 회사 관리자**.
 *   게시판 운영자는 서버가 남의 댓글 삭제를 허락하지 않으므로 단추를 주지 않는다(누르면 403 이 되는 단추를 두지 않는다).
 * ★ 답글은 뿌리 댓글에만 — 서버가 답글의 답글을 거절한다(CommentThread 도 답글에는 「답글」을 두지 않는다).
 * ★ 삭제된 댓글은 서버가 목록에서 빼므로 수에 들어가지 않는다.
 * ★ 수정 · 답글은 아래 입력칸 하나로 한다 — 입력칸이 여럿 열리면 무엇을 올리는지 헷갈린다.
 */

type ApiError = { response?: { status?: number; data?: { message?: string; code?: string } }; message?: string };
const errMsg = (e: unknown) => (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown';

/**
 * D3 (2026-09-15 실측) — R12(PR #1382) 뒤 옛 댓글 경로는 **볼 수 없는 글**을 없는 글과 같게 404 `POST_NOT_FOUND` 로 끊는다.
 * 그때는 오류 문장이 아니라 글 보기의 「열람 권한 없음」과 같은 표시로 바꾼다. 댓글이 없어진 404(`COMMENT_NOT_FOUND`)
 * 나 그 밖 오류는 종전 문장 그대로.
 */
const isPostGone = (e: unknown) => {
  const res = (e as ApiError)?.response;
  return res?.status === 404 && res.data?.code === 'POST_NOT_FOUND';
};

const ADMIN_ROLES = new Set(['ADMIN', 'SYSTEM_ADMIN']);

export interface BoardPostCommentsProps {
  post: BoardPostDetail;
  onCountChange: (count: number) => void;
}

export function BoardPostComments({ post, onCountChange }: BoardPostCommentsProps) {
  const me = useAuthStore((s) => s.user);
  const [comments, setComments] = useState<BoardCommentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<BoardCommentDto | null>(null);
  const [editing, setEditing] = useState<BoardCommentDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [attempt, setAttempt] = useState(0);
  /** D3 — 볼 수 없는 글(404 POST_NOT_FOUND). 켜지면 목록 · 입력칸 대신 「열람 권한 없음」 표시만 남는다. */
  const [denied, setDenied] = useState(false);

  const countRef = useRef(onCountChange);
  countRef.current = onCountChange;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listBoardPostComments(post.id)
      .then((rows) => {
        if (cancelled) return;
        setComments(rows);
        setError(null);
        countRef.current(countComments(rows));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (isPostGone(e)) { setDenied(true); return; }
        setError(tBoards('boards.v1.post.comments.load_err', { msg: errMsg(e) }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [post.id, attempt]);

  const meId = me ? String(me.id) : null;
  const isAdmin = !!me && ADMIN_ROLES.has(me.role);
  const isPostAuthor = meId != null && post.author.id === meId;

  const byId = useMemo(() => {
    const map = new Map<string, BoardCommentDto>();
    const walk = (rows: readonly BoardCommentDto[]) => rows.forEach((c) => { map.set(c.id, c); walk(c.replies ?? []); });
    walk(comments);
    return map;
  }, [comments]);

  const toItem = useCallback((c: BoardCommentDto): CommentItem => ({
    id: c.id,
    author: { id: Number(c.author.id), name: c.author.name },
    body: c.content,
    createdAt: c.createdAt,
    canEdit: meId === c.author.id,
    canDelete: meId === c.author.id || isPostAuthor || isAdmin,
    replies: (c.replies ?? []).map((r) => toItem(r)),
  }), [meId, isPostAuthor, isAdmin]);

  const reset = () => { setDraft(''); setReplyTo(null); setEditing(null); };

  async function submit() {
    const content = draft.trim();
    if (!content) return;
    setSubmitting(true);
    setError(null);
    try {
      if (editing) await updateBoardComment(editing.id, content);
      else await createBoardPostComment(post.id, { content, parentCommentId: replyTo?.id ?? null });
      reset();
      setAttempt((n) => n + 1);
    } catch (e) {
      if (isPostGone(e)) { reset(); setDenied(true); return; }
      setError(tBoards('boards.v1.post.comments.err', { msg: errMsg(e) }));
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(item: CommentItem) {
    const ok = await layerConfirm(tBoards('boards.v1.post.comments.delete_confirm'), { danger: true });
    if (!ok) return;
    try {
      await deleteBoardComment(item.id);
      if (editing?.id === item.id || replyTo?.id === item.id) reset();
      setAttempt((n) => n + 1);
    } catch (e) {
      if (isPostGone(e)) { reset(); setDenied(true); return; }
      setError(tBoards('boards.v1.post.comments.err', { msg: errMsg(e) }));
    }
  }

  if (denied) {
    return (
      <div className="bv1-comments">
        <EmptyState
          kind="no-permission"
          icon={Lock}
          title={tBoards('boards.v1.post.deny.title')}
          description={tBoards('boards.v1.post.deny.desc')}
        />
      </div>
    );
  }

  const label = editing
    ? tBoards('boards.v1.post.comments.editing')
    : replyTo
      ? tBoards('boards.v1.post.comments.reply_to', { name: replyTo.author.name })
      : tBoards('boards.v1.post.comments.title');

  return (
    <div className="bv1-comments">
      {error && <InlineNote tone="danger">{error}</InlineNote>}
      {loading && comments.length === 0 ? (
        <p className="bv1-muted" aria-busy="true">{tBoards('boards.v1.loading')}</p>
      ) : (
        <CommentThread
          comments={comments.map(toItem)}
          onReply={(item) => { setEditing(null); setReplyTo(byId.get(item.id) ?? null); setDraft(''); }}
          onEdit={(item) => {
            const c = byId.get(item.id);
            if (!c) return;
            setReplyTo(null);
            setEditing(c);
            setDraft(c.content);
          }}
          onDelete={(item) => void remove(item)}
          emptyText={post.commentsAllowed ? tBoards('boards.v1.post.comments.empty') : undefined}
          locked={post.commentsAllowed ? undefined : tBoards('boards.v1.post.comments.locked')}
          composer={{
            value: draft,
            onChange: setDraft,
            onSubmit: () => void submit(),
            onCancel: replyTo || editing ? reset : undefined,
            label,
            placeholder: tBoards('boards.v1.post.comments.placeholder'),
            /*
             * 댓글 상한 (2026-09-23 사용자 결정 · 종전 5,000).
             *
             * ⛔ **서버만 줄이면 사용자가 «쓴 뒤에» 안다** — 길게 쓰고 저장을 눌러야 400 이다.
             *   여기서 막으면 **쓰는 동안** 알고, 화면이 「n / 1,000」을 그린다.
             * ⚠ 이 수는 서버의 `COMMENT_CONTENT_MAX` 와 **같아야 한다**(chat-server
             *   `board-comment.routes.ts`). 어긋나면 화면은 받아 주는데 저장이 400 이 된다.
             * ★ 이미 그보다 긴 **옛 댓글을 고칠 때**는 서버가 그 길이까지 받아 준다 —
             *   그때는 이 상한이 화면에서 먼저 막으므로, 줄이는 방향으로만 고칠 수 있다.
             */
            maxLength: 1000,
            submitting,
            hint: tBoards('boards.v1.post.comments.hint'),
          }}
        />
      )}
    </div>
  );
}
